import ProductGroupDropdown from "components/dropdowns/reactselect/ProductGroupDropdown";
import ProductTypesDropdown from "components/dropdowns/reactselect/ProductTypesDropdown";
import VatRateDropdown from "components/dropdowns/reactselect/VatRateDropdown";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import React, { useState } from "react";
import { getMoneyFormatter } from "utilities/formatter";
import { ProductTemplateInfo } from "./models";
import { usePriceState } from "./usePriceState";

interface ProductTemplateDetailSectionProps {
  productGroups: server.dto.ProductGroup[]
  productTemplate: server.dto.ProductTemplate
  vatRates: server.dto.VatRate[]
}

export const ProductTemplateDetailSection = (props: ProductTemplateDetailSectionProps) => {
  const [productTemplate, setProductTemplate] = useState<ProductTemplateInfo>({
    productType: props.productTemplate.productType,
    productGroup: props.productTemplate.productGroup,
    ledgerNumber: props.productTemplate.ledgerNumber,
    internalName: props.productTemplate.title,
    title: props.productTemplate.titleForTemplate,
    description: props.productTemplate.descriptionForTemplate || "",
    unit: props.productTemplate.unitForTemplate || "",
  });
  const [costsExVat, costsIncVat, costsIsIncVat, costsVatRate, onCostsVatRateChange, onCostsValueChange] = usePriceState(
    props.vatRates.find(vr => vr.vatRateId === props.productTemplate.costsVatRateId), props.productTemplate.costsIsIncVat, props.productTemplate.costsPricePerUnitExVat);
  const [salesExVat, salesIncVat, salesIsIncVat, salesVatRate, onSalesVatRateChange, onSalesValueChange] = usePriceState(
    props.vatRates.find(vr => vr.vatRateId === props.productTemplate.salesVatRateId), props.productTemplate.salesIsIncVat, props.productTemplate.salesPricePerUnitExVat);

  const onValueChanged = (key: string, value: any) => {
    if (key === "productType") {
      setProductTemplate({ ...productTemplate, productType: value });
    } else if (key === "productGroup") {
      setProductTemplate({ ...productTemplate, productGroup: value });
    } else if (key === "internalName") {
      setProductTemplate({ ...productTemplate, internalName: value });
    } else if (key === "ledger") {
      setProductTemplate({ ...productTemplate, ledgerNumber: value });
    } else if (key === "title") {
      setProductTemplate({ ...productTemplate, title: value });
    } else if (key === "description") {
      setProductTemplate({ ...productTemplate, description: value });
    } else if (key === "unit") {
      setProductTemplate({ ...productTemplate, unit: value });
    } else if (key === "costsVatRate") {
      onCostsVatRateChange(value);
    } else if (key === "costsExcludingTax") {
      onCostsValueChange(false, value);
    } else if (key === "costsIncludingTax") {
      onCostsValueChange(true, value);
    } else if (key === "salesVatRate") {
      onSalesVatRateChange(value);
    } else if (key === "salesExcludingTax") {
      onSalesValueChange(false, value);
    } else if (key === "salesIncludingTax") {
      onSalesValueChange(true, value);
    }
  }

  const moneyFormatter = getMoneyFormatter();

  const getUpdateProductTemplateInstruction = (pt: ProductTemplateInfo): server.dto.UpdateProductTemplateInstruction => ({
    costsPricePerUnit: costsIsIncVat ? costsIncVat : costsExVat,
    costsVatRateId: costsVatRate.vatRateId,

    descriptionForTemplate: pt.description,
    ledgerNumber: pt.ledgerNumber,
    productGroupId: pt.productGroup?.productGroupId || null,
    productTemplateId: props.productTemplate.productTemplateId,
    productType: pt.productType.value,

    salesPricePerUnit: salesIsIncVat ? salesIncVat : salesExVat,
    salesVatRateId: salesVatRate.vatRateId,

    title: pt.internalName,
    titleForTemplate: pt.title,
    unitForTemplate: pt.unit,
  });

  return <DetailSectionTemplate
    isInPageEditable
    sections={[{
      type: "regular",
      key: "productTemplate",
      title: RESX.ProductTemplate.resxProductTemplate,
      fields: [{
        key: "productType",
        label: RESX.ProductTemplate.ProductType,
        render: () => <div>{productTemplate.productType.text}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.ProductType>) => <ProductTypesDropdown
            value={value}
            onChange={(productType) => {
              onChange(productType, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: productTemplate.productType,
          submitChange: (productType: server.dto.ProductType) => window.CoreApi.ProductTemplates.Update({
            ...getUpdateProductTemplateInstruction(productTemplate),
            productType: productType.value,
          }),
        }
      }, {
        key: "productGroup",
        label: RESX.ProductGroup.resxProductGroup,
        render: () => <div>{productTemplate.productGroup.title}</div>,
        hidden: !site.currentEnvironment.hasProductGroups,
        isEmpty: !productTemplate.productGroup,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.ProductGroup>) => <ProductGroupDropdown
            options={props.productGroups}
            value={value}
            onChange={(productGroup) => {
              onChange(productGroup, true);
            }}
            onEsc={() => cancel()}
            config={{
              isClearable: true,
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: productTemplate.productGroup,
          submitChange: (productGroup: server.dto.ProductGroup) => window.CoreApi.ProductTemplates.Update({
            ...getUpdateProductTemplateInstruction(productTemplate),
            productGroupId: productGroup?.productGroupId || null,
          }),
        }
      }, {
        key: "internalName",
        label: RESX.ProductTemplate.InternalName,
        render: () => <div>{productTemplate.internalName}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
            validatorKey="internalName"
            focusOnMount
            placeholder={RESX.ProductTemplate.ProductCodeForOwnUse}
            maxLength={serverReference.constants.productTemplate.maxTitleLength}
            value={value}
            onChange={(internalName) => { onChange(internalName); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: productTemplate.internalName,
          submitChange: (internalName: string) => window.CoreApi.ProductTemplates.Update({
            ...getUpdateProductTemplateInstruction(productTemplate),
            title: internalName,
          }),
        },
      }, {
        key: "ledger",
        label: RESX.ProductGroup.Ledger,
        supportArticleId: serverReference.supportArticleIds.whatIsTheLedgerFor,
        render: () => <div>{productTemplate.ledgerNumber}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <RequiredNumberInput
            autoFocus
            width={75}
            validatorKey="ledger"
            minValue={1}
            maxValue={Math.pow(10, serverReference.constants.productTemplate.maxLedgerNumberLength) - 1}
            value={value}
            onChange={(v: number) => onChange(v)}
            onEnterPressed={(v: number) => onChange(v, true)}
            onEscPressed={() => cancel()}
            decimalPoints={0}
          />,
          type: "confirmOrCancelButton",
          initialValue: productTemplate.ledgerNumber,
          submitChange: (ledgerNumber: number) => window.CoreApi.ProductTemplates.Update({
            ...getUpdateProductTemplateInstruction(productTemplate),
            ledgerNumber,
          }),
        },
      }, {
        key: "title",
        label: RESX.GeneralLabels.Title,
        render: () => <div>{productTemplate.title}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
            validatorKey="title"
            focusOnMount
            maxLength={serverReference.constants.productTemplate.maxTitleForTemplateLength}
            value={value}
            onChange={(title) => { onChange(title); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: productTemplate.title,
          submitChange: (title: string) => window.CoreApi.ProductTemplates.Update({
            ...getUpdateProductTemplateInstruction(productTemplate),
            titleForTemplate: title,
          }),
        },
      }, {
        key: "description",
        label: RESX.GeneralLabels.Description,
        render: () => <OptionalTextAreaDetails>{productTemplate.description}</OptionalTextAreaDetails>,
        isEmpty: !productTemplate.description,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
            focusOnMount
            maxLength={serverReference.constants.productTemplate.maxDescriptionForTemplateLength}
            value={value}
            onChange={(desc) => { onChange(desc); }}
            onCtrlEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: productTemplate.description,
          submitChange: (desc: string) => window.CoreApi.ProductTemplates.Update({
            ...getUpdateProductTemplateInstruction(productTemplate),
            descriptionForTemplate: desc,
          }),
        },
      }, {
        key: "unit",
        label: RESX.ProductTemplate.Unit,
        supportArticleId: serverReference.supportArticleIds.unitInProduct,
        render: () => <div>{productTemplate.unit}</div>,
        isEmpty: !productTemplate.unit,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
            focusOnMount
            maxLength={serverReference.constants.productTemplate.maxUnitLength}
            value={value}
            onChange={(unit) => { onChange(unit); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: productTemplate.unit,
          submitChange: (unit: string) => window.CoreApi.ProductTemplates.Update({
            ...getUpdateProductTemplateInstruction(productTemplate),
            unitForTemplate: unit,
          }),
        },
      }]
    }, {
      type: "regular",
      key: "costs",
      title: RESX.Financial.CostsPerUnit,
      fields: [{
        key: "costsVatRate",
        label: RESX.Financial.TaxRate,
        render: () => <div>{costsVatRate.internalName} ({costsVatRate.percentageFormatted})</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.VatRate>) => <VatRateDropdown
            options={props.vatRates}
            value={value}
            onChange={(vatRate) => {
              onChange(vatRate, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: costsVatRate,
          submitChange: (vatRate: server.dto.VatRate) => window.CoreApi.ProductTemplates.Update({
            ...getUpdateProductTemplateInstruction(productTemplate),
            costsVatRateId: vatRate.vatRateId,
          }),
        }
      }, {
        key: "costsExcludingTax",
        label: RESX.Financial.PriceExcludingTax,
        render: () => <div>{moneyFormatter.format(costsExVat)}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <OptionalNumberInput
            autoFocus
            canBeNegative
            width={90}
            value={value}
            onChange={(v: number) => onChange(v)}
            onEnterPressed={(v: number) => onChange(v, true)}
            onEscPressed={() => cancel()}
            decimalPoints={2}
          />,
          type: "confirmOrCancelButton",
          initialValue: costsExVat,
          submitChange: (amount: number) => window.CoreApi.ProductTemplates.Update({
            ...getUpdateProductTemplateInstruction(productTemplate),
            costsPricePerUnit: amount,
          }),
        },
      }, {
        key: "costsIncludingTax",
        label: RESX.Financial.PriceIncludingTax,
        render: () => <div>{moneyFormatter.format(costsIncVat)}</div>,
      }]
    }, {
      type: "regular",
      key: "sales",
      title: RESX.Financial.SalesPerUnit,
      fields: [{
        key: "salesVatRate",
        label: RESX.Financial.TaxRate,
        render: () => <div>{salesVatRate.internalName} ({salesVatRate.percentageFormatted})</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.VatRate>) => <VatRateDropdown
            options={props.vatRates}
            value={value}
            onChange={(vatRate) => {
              onChange(vatRate, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: salesVatRate,
          submitChange: (vatRate: server.dto.VatRate) => window.CoreApi.ProductTemplates.Update({
            ...getUpdateProductTemplateInstruction(productTemplate),
            salesVatRateId: vatRate.vatRateId,
          }),
        }
      }, {
        key: "salesExcludingTax",
        label: RESX.Financial.PriceExcludingTax,
        render: () => <div>{moneyFormatter.format(salesExVat)}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <OptionalNumberInput
            autoFocus
            canBeNegative
            width={90}
            value={value}
            onChange={(v: number) => onChange(v)}
            onEnterPressed={(v: number) => onChange(v, true)}
            onEscPressed={() => cancel()}
            decimalPoints={2}
          />,
          type: "confirmOrCancelButton",
          initialValue: salesExVat,
          submitChange: (amount: number) => window.CoreApi.ProductTemplates.Update({
            ...getUpdateProductTemplateInstruction(productTemplate),
            salesPricePerUnit: amount,
          }),
        },
      }, {
        key: "salesIncludingTax",
        label: RESX.Financial.PriceIncludingTax,
        render: () => <div>{moneyFormatter.format(salesIncVat)}</div>,
      }]
    }]}
    onValueChanged={onValueChanged}
  />
}